/// <reference lib="dom"/>
import * as rxjs from "rxjs";
import { type ActionHandlers, Dispatcher } from "@utils/dispatch";
import { createDispatchContext } from "@uiw/utils";
import { WbaCtx } from "@wbaudio/core/wbactx";
import { RenderSampleGen, SampleGen, SampleGenPreview } from "@wbaudio/core/synth";
import { EncodeWav } from "@wbaudio/utils";
import { AppMetaInfo } from "@/spa/context/sw.tsx";
import { LoopPlayer } from "./player.tsx";
import {
  LoadLoop,
  LoadShushStored,
  SameSampleState,
  SaveLoop,
  SaveShushStored,
  ShusherLoopDefaults,
  type ShushPreset,
} from "./presets.tsx";

export const ShushViews = ["play", "config"] as const;
export type ShushView = (typeof ShushViews)[number];

export const LoopStatuses = ["missing", "rendering", "ready", "error"] as const;
export type LoopStatus = (typeof LoopStatuses)[number];

export interface ShushAppState {
  view: ShushView;
  playing: boolean;
  volume: number;
  presets: Record<string, ShushPreset>;
  active: string;
  dirty: boolean;
  loop: LoopStatus;
  "loop/samples": Float32Array | null;
  error: string | null;
}

export interface ShushAppActions {
  init: undefined;
  view: { view: ShushView };
  play: undefined;
  stop: undefined;
  playing: { playing: boolean };
  volume: { volume: number };
  "edit/reset": undefined;
  save: undefined;
  dirty: { dirty: boolean };
  "loop/rendering": undefined;
  "loop/ready": { samples: Float32Array };
  "loop/error": { message: string };
  error: { message: string };
}

type Handlers = ActionHandlers<ShushAppActions, ShushAppState>;

function ErrorMessage(e: unknown) {
  return e instanceof Error ? e.message : String(e);
}

async function DecodeLoop(blob: Blob, sampleRate: number): Promise<AudioBuffer> {
  const ctx = new OfflineAudioContext(1, 1, sampleRate);
  return await ctx.decodeAudioData(await blob.arrayBuffer());
}

export function ActivePreset(state: ShushAppState): ShushPreset {
  return state.presets[state.active];
}

export class ShushApp extends Dispatcher<ShushAppActions, ShushAppState, undefined> {
  protected readonly handlers: Handlers = {
    init: ({ state }) => {
      const preset = ActivePreset(state);
      this.gen.dispatch({ type: "set", payload: { state: preset.state } });
      this.player.volume(state.volume);
      this.player.session(preset.name, [], {
        play: () => this.dispatch({ type: "play" }),
        stop: () => this.dispatch({ type: "stop" }),
      });
      this.#loadLoop(preset);
      return state;
    },
    view: ({ state, payload }) => {
      if (payload.view === state.view) return state;
      if (payload.view === "config") this.player.stop();
      if (state.view === "config") {
        this.preview.dispatch({ type: "stop" });
        if (state.dirty) {
          this.gen.dispatch({ type: "set", payload: { state: ActivePreset(state).state } });
        }
      }
      return { ...state, view: payload.view };
    },
    play: ({ state }) => {
      this.player.play().catch((e) =>
        this.dispatch({ type: "error", payload: { message: ErrorMessage(e) } })
      );
      return state;
    },
    stop: ({ state }) => {
      this.player.stop();
      return state;
    },
    playing: ({ state, payload }) => ({ ...state, playing: payload.playing }),
    volume: ({ state, payload }) => {
      const volume = Math.min(1, Math.max(0, payload.volume));
      this.player.volume(volume);
      const next = { ...state, volume };
      this.#persist(next);
      return next;
    },
    "edit/reset": ({ state }) => {
      this.gen.dispatch({ type: "set", payload: { state: structuredClone(ShusherLoopDefaults) } });
      return state;
    },
    save: ({ state }) => {
      const preset = { ...ActivePreset(state), state: this.gen.state };
      const next = { ...state, presets: { ...state.presets, [preset.id]: preset } };
      this.#persist(next);
      this.#render(preset);
      return next;
    },
    dirty: ({ state, payload }) => ({ ...state, dirty: payload.dirty }),
    "loop/rendering": ({ state }) => ({ ...state, loop: "rendering", error: null }),
    "loop/ready": ({ state, payload }) => ({
      ...state,
      loop: "ready",
      "loop/samples": payload.samples,
    }),
    "loop/error": ({ state, payload }) => ({ ...state, loop: "error", error: payload.message }),
    error: ({ state, payload }) => ({ ...state, error: payload.message }),
  };

  readonly wbactx = new WbaCtx({ trackGraph: false });
  readonly gen = new SampleGen();
  readonly preview: SampleGenPreview;
  readonly player = new LoopPlayer(this.wbactx);

  #subs = new rxjs.Subscription();
  #renderSeq = 0;

  constructor() {
    const stored = LoadShushStored();
    super(undefined, {
      view: "play",
      playing: false,
      volume: stored.volume,
      presets: stored.presets,
      active: stored.active,
      dirty: false,
      loop: "missing",
      "loop/samples": null,
      error: null,
    });
    this.preview = new SampleGenPreview({
      wbactx: this.wbactx,
      gen: this.gen,
      base: AppMetaInfo.base,
    });

    this.#subs.add(
      this.player.playing$.subscribe((playing) =>
        this.dispatch({ type: "playing", payload: { playing } })
      ),
    );
    this.#subs.add(
      rxjs.combineLatest([this.gen.state$, this.select$((s) => ActivePreset(s).state)]).pipe(
        rxjs.map(([edited, saved]) => !SameSampleState(edited, saved)),
        rxjs.distinctUntilChanged(),
      ).subscribe((dirty) => this.dispatch({ type: "dirty", payload: { dirty } })),
    );
  }

  #persist(state: ShushAppState) {
    SaveShushStored({ active: state.active, volume: state.volume, presets: state.presets });
  }

  async #loadLoop(preset: ShushPreset) {
    try {
      const blob = await LoadLoop(preset.id);
      if (!blob) return this.#render(preset);
      const buffer = await DecodeLoop(blob, preset.state.sampleRate);
      this.player.load(buffer);
      this.dispatch({ type: "loop/ready", payload: { samples: buffer.getChannelData(0) } });
    } catch (e) {
      this.dispatch({ type: "loop/error", payload: { message: ErrorMessage(e) } });
    }
  }

  async #render(preset: ShushPreset) {
    const seq = ++this.#renderSeq;
    this.dispatch({ type: "loop/rendering" });
    try {
      const buffer = await RenderSampleGen(preset.state, { base: AppMetaInfo.base });
      const blob = EncodeWav(buffer);
      await SaveLoop(preset.id, blob);
      if (seq !== this.#renderSeq) return;
      this.player.load(buffer);
      this.dispatch({ type: "loop/ready", payload: { samples: buffer.getChannelData(0) } });
    } catch (e) {
      if (seq !== this.#renderSeq) return;
      this.dispatch({ type: "loop/error", payload: { message: ErrorMessage(e) } });
    }
  }

  override dispose() {
    if (this.disposed) return;
    this.#subs.unsubscribe();
    this.preview.dispose();
    this.gen.dispose();
    this.player.dispose();
    this.wbactx.dispose();
    super.dispose();
  }
}

export const ShushAppContext = createDispatchContext<ShushApp>("ShushApp");
export const useShushApp = ShushAppContext.useDispatcher;
export const useShushAppSelect = ShushAppContext.useSelector;
